import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Tag,
  Upload,
  UploadFile
} from 'antd'
import { FormInstance } from 'antd/lib'

import {
  ClassifierCategory,
  ClassifierType,
  InventoryDocumentState,
  ReturnDocumentResponse
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CustomItemRender,
  Text,
  ClassifierInfiniteSelect
} from 'src/components'
import { CounterPartSelect } from 'src/features/counterParts/components'
import { getEntryStatus } from 'src/features/stock'
import { useStyles } from 'src/features/stock/pages/settlements/styles'
import { onDownloadFile, useClassifierId } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { ReturnsConsumablesTable } from './ReturnsConsumablesTable'

type Props = {
  form: FormInstance
  item?: ReturnDocumentResponse
}

type Document = UploadFile & { id?: string }

export const ReturnDetails = ({ form, item }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles({ hasId: true })

  const medicineItems = Form.useWatch('medicine', form)

  const total = medicineItems?.reduce(
    (acc: number, item: { priceTotal: number }) =>
      acc + (item?.priceTotal ?? 0),
    0
  )
  const reasonsId = useClassifierId(
    ClassifierType.RETURN_REASON,
    ClassifierCategory.STOCKS
  )

  const typedState = item?.state as unknown as InventoryDocumentState

  return (
    <Flex vertical gap={24}>
      <Card>
        <Flex vertical gap={24}>
          <Flex justify="space-between" align="center">
            <Text weight={700} size="xxl">
              {t('stock_returns.stock_return')}
            </Text>

            {!!typedState && (
              <Tag color={getEntryStatus(t, typedState)?.color}>
                {getEntryStatus(t, typedState)?.text}
              </Tag>
            )}
          </Flex>

          <Card className={styles.detailsCard}>
            <Flex vertical gap={24}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="invoiceDate"
                    label={t(`stock.invoice_date`)}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      format={dayMonthYearBackSlash}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="invoiceNumber"
                    label={t(`stock.accompanying_invoice`)}
                    rules={requiredRules}
                  >
                    <Input
                      className="full-width"
                      disabled
                      placeholder={t(`stock.accompanying_invoice`)}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="purchase_center"
                    label={t(`stock.provider`)}
                    rules={requiredRules}
                  >
                    <Select />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="supplier"
                    label={t(`counterparts.purchase_center`)}
                    rules={requiredRules}
                  >
                    <Input
                      className="full-width"
                      disabled
                      placeholder={t(`counterparts.purchase_center`)}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="takeoverDate"
                    label={t(`stock_administration.pickup_date`)}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      disabled
                      format={dayMonthYearBackSlash}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="reasons"
                    label={t(`stock.return_reason`)}
                    rules={requiredRules}
                  >
                    <ClassifierInfiniteSelect id={reasonsId} mode="multiple" />
                  </Form.Item>
                </Col>
              </Row>

              {item?.transporter && (
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <Form.Item
                      name="transporterId"
                      label={t(`stock_administration.select_transport`)}
                      rules={requiredRules}
                    >
                      <CounterPartSelect
                        allowClear={false}
                        placeholder={t(`stock_administration.select_transport`)}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="transporterAccompanyingNotice"
                      label={t(`stock_administration.way_bill`)}
                      rules={requiredRules}
                    >
                      <Input
                        className="full-width"
                        placeholder={t(`stock_administration.way_bill`)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Flex>
          </Card>
        </Flex>
      </Card>

      <Card>
        <Card title={t('stock.stocks')}>
          <Form.List name="medicine">
            {(fields, { add, remove }) => (
              <ReturnsConsumablesTable
                item={item}
                fields={fields}
                add={add}
                remove={remove}
                form={form}
              />
            )}
          </Form.List>

          <Flex justify="end" align="center" gap={8}>
            <Text weight={700}>{t('common.total')}: </Text>
            <Text weight={700}>{total} MDL</Text>
          </Flex>
        </Card>
      </Card>

      <Card>
        <Card className={styles.detailsCard}>
          <Flex vertical gap={24}>
            <Card
              title={t('stock_applications.uploaded_documents')}
              className={styles.extraCardStyles}
            >
              <Upload
                itemRender={(_, file: Document) => (
                  <CustomItemRender
                    file={file}
                    onDownload={() => onDownloadFile(t, file?.id, file?.name)}
                  />
                )}
                defaultFileList={item?.mediaFiles?.map((file, index) => ({
                  ...file,
                  uid: String(index),
                  name: file?.name || `file-${index}`
                }))}
                disabled
              />
            </Card>

            {item?.comment && (
              <Card
                title={t('common.comment')}
                className={styles.extraCardStyles}
              >
                {item?.comment}
              </Card>
            )}
          </Flex>
        </Card>
      </Card>
    </Flex>
  )
}
